import React, { useState, useEffect } from 'react';
import {
  Typography, Drawer, TextField, IconButton,
  Avatar, Stack, List, ListItem, ListItemAvatar, Divider, Box,
  Menu, MenuItem, InputAdornment, useMediaQuery, useTheme
} from '@mui/material';
import { Send, Close, FavoriteBorder, Favorite, ChatBubbleOutline, MoreVert, Edit, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { db, auth } from './firebase';
import {
  collection, addDoc, deleteDoc, query, orderBy, onSnapshot,
  serverTimestamp, doc, updateDoc, increment, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import { useTranslatedDictionary } from './hooks/useTranslatedDictionary';

const COMMENTS_TEXT = {
  es: {
    user: 'Usuario',
    comments: 'Comentarios',
    replyPlaceholder: 'Responder...',
    writeComment: 'Escribe un comentario...',
    edit: 'Editar',
    delete: 'Eliminar',
    noCommentsTitle: 'Todavía no hay comentarios',
    noCommentsSubtitle: 'Sé el primero en decir algo sobre esta publicación.',
  },
  en: {
    user: 'User',
    comments: 'Comments',
    replyPlaceholder: 'Reply...',
    writeComment: 'Write a comment...',
    edit: 'Edit',
    delete: 'Delete',
    noCommentsTitle: 'No comments yet',
    noCommentsSubtitle: 'Be the first to say something about this post.',
  },
};

// --- COMPONENTE PARA RENDERIZAR RESPUESTAS ---
const ListaRespuestas = ({ postId, commentId, goToProfile, isLightTheme = false, user, t, onSurface, zIndex, surfaceBg }) => {
  const [respuestas, setRespuestas] = useState([]);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editReplyText, setEditReplyText] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuReplyId, setMenuReplyId] = useState(null);

  useEffect(() => {
    if (!postId || !commentId) return;
    const q = query(
      collection(db, 'posts', postId, 'comments', commentId, 'replies'),
      orderBy('timestamp', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setRespuestas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [postId, commentId]);

  const handleSaveEditReply = async (replyId) => {
    if (!editReplyText.trim()) return;
    try {
      await updateDoc(
        doc(db, 'posts', postId, 'comments', commentId, 'replies', replyId),
        { text: editReplyText.trim() }
      );
    } catch (error) { console.error(error); }
    setEditingReplyId(null);
  };

  const handleDeleteReply = async (replyId) => {
    try {
      await deleteDoc(doc(db, 'posts', postId, 'comments', commentId, 'replies', replyId));
      await updateDoc(doc(db, 'posts', postId, 'comments', commentId), { repliesCount: increment(-1) });
    } catch (error) { console.error(error); }
    setMenuAnchor(null);
  };

  if (respuestas.length === 0) return null;

  return (
    <Box sx={{
      ml: { xs: 4, sm: 6 }, // Menos margen en móviles
      mt: 1,
      borderLeft: '1px solid rgba(0,212,255,0.2)',
      pl: { xs: 1.5, sm: 2 }
    }}>
      {respuestas.map((r) => (
        <Stack key={r.id} direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Avatar
            src={r.userImg}
            onClick={() => goToProfile(r.uid)}
            sx={{ width: 28, height: 28, cursor: 'pointer', border: '1px solid rgba(0,212,255,0.2)' }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                variant="caption"
                onClick={() => goToProfile(r.uid)}
                sx={{ color: '#00d4ff', fontWeight: 600, cursor: 'pointer', display: 'block' }}
              >
                {r.username}
              </Typography>
              {user?.uid === r.uid && (
                <IconButton
                  size="small"
                  onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuReplyId(r.id); }}
                  sx={{ color: onSurface(0.4), p: 0.4 }}
                >
                  <MoreVert sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Stack>

            {editingReplyId === r.id ? (
              <Box mt={0.5}>
                <TextField
                  fullWidth
                  size="small"
                  value={editReplyText}
                  onChange={(e) => setEditReplyText(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSaveEditReply(r.id);
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => handleSaveEditReply(r.id)} disabled={!editReplyText.trim()} sx={{ color: '#00d4ff' }}>
                          <Send sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { color: onSurface(1), bgcolor: onSurface(0.05), borderRadius: '10px', fontSize: '0.85rem' } }}
                />
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: isLightTheme ? '#1a2028' : '#eee', fontSize: '0.85rem', lineHeight: 1.4 }}>
                {r.text}
              </Typography>
            )}
          </Box>
        </Stack>
      ))}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        sx={{ zIndex: zIndex + 2 }}
        PaperProps={{ sx: { bgcolor: surfaceBg, color: onSurface(1) } }}
      >
        <MenuItem onClick={() => { const r = respuestas.find(x => x.id === menuReplyId); setEditingReplyId(r.id); setEditReplyText(r.text); setMenuAnchor(null); }}>
          <Edit sx={{ fontSize: 18, mr: 1, color: '#00d4ff' }} /> {t('edit')}
        </MenuItem>
        <MenuItem onClick={() => handleDeleteReply(menuReplyId)}>
          <Delete sx={{ fontSize: 18, mr: 1, color: '#ff6b6b' }} /> {t('delete')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

// --- COMPONENTE PRINCIPAL ---
const Comentarios = ({ open, handleClose, postId, onOpenProfile, zIndex = 1300, language = 'es', themeMode = 'oscuro' }) => {
  const isLightTheme = themeMode === 'claro';
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const surfaceBg = isLightTheme ? '#ffffff' : '#1a1a1a';
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md')); // Movil + tablet
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [replyText, setReplyText] = useState('');
  const [listaComentarios, setListaComentarios] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [user, setUser] = useState(() => auth.currentUser);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCommentId, setMenuCommentId] = useState(null);
  const navigate = useNavigate();
  const t = useTranslatedDictionary(COMMENTS_TEXT, language);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setUser(u));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!postId) return;
    const q = query(collection(db, 'posts', postId, 'comments'), orderBy('timestamp', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const comentarios = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setListaComentarios(comentarios);
      const postRef = doc(db, 'posts', postId);
      updateDoc(postRef, { commentsCount: snapshot.docs.length }).catch(() => {});
    }, (error) => {
      console.error('Error cargando comentarios:', error);
    });
    return () => unsubscribe();
  }, [postId]);

  const enviarComentario = async () => {
    const texto = replyText.trim();
    if (!texto || !user || !replyingTo) return;
    setReplyText('');
    setReplyingTo(null);
    try {
      const repliesRef = collection(db, 'posts', postId, 'comments', replyingTo, 'replies');
      await addDoc(repliesRef, {
        uid: user.uid,
        text: texto,
        username: user.displayName || t('user'),
        userImg: user.photoURL || 'https://i.pravatar.cc/150',
        timestamp: serverTimestamp(),
        likesCount: 0,
        likedByUsers: [],
      });
      const commentRef = doc(db, 'posts', postId, 'comments', replyingTo);
      await updateDoc(commentRef, { repliesCount: increment(1) });
    } catch (error) { console.error(error); }
  };

  const enviarComentarioNuevo = async () => {
    const texto = nuevoComentario.trim();
    if (!texto || !user) return;
    setNuevoComentario('');
    try {
      await addDoc(collection(db, 'posts', postId, 'comments'), {
        uid: user.uid,
        text: texto,
        username: user.displayName || t('user'),
        userImg: user.photoURL || 'https://i.pravatar.cc/150',
        timestamp: serverTimestamp(),
        likesCount: 0,
        likedByUsers: [],
        repliesCount: 0,
      });
    } catch (error) { console.error(error); }
  };

  const toggleLike = async (commentId, likedByUsers = []) => {
    if (!user) return;
    const userLiked = likedByUsers?.includes(user.uid);
    const commentRef = doc(db, 'posts', postId, 'comments', commentId);
    try {
      await updateDoc(commentRef, userLiked
        ? { likedByUsers: arrayRemove(user.uid), likesCount: increment(-1) }
        : { likedByUsers: arrayUnion(user.uid), likesCount: increment(1) }
      );
    } catch (error) { console.error(error); }
  };

  const handleSaveEdit = async (commentId) => {
    if (!editText.trim()) return;
    try {
      await updateDoc(doc(db, 'posts', postId, 'comments', commentId), { text: editText.trim() });
    } catch (error) { console.error(error); }
    setEditingId(null);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteDoc(doc(db, 'posts', postId, 'comments', commentId));
    } catch (error) { console.error(error); }
    setMenuAnchor(null);
  };

  const goToProfile = (uid) => {
    if (onOpenProfile) {
      onOpenProfile(uid);
    } else {
      navigate(`/perfilusuario/${uid}`);
    }
    handleClose();
  };

  return (
    <Drawer 
      anchor="bottom"
      open={open} 
      onClose={handleClose} 
      sx={{ zIndex }}
      PaperProps={{
        sx: {
          height: isCompact ? { xs: '92dvh', sm: '86dvh', md: '82dvh' } : '88dvh',
          width: '100%',
          maxWidth: '600px',
          bgcolor: surfaceBg,
          color: onSurface(1),
          borderRadius: '24px 24px 0 0',
          px: { xs: 0.25, sm: 2.25 },
          pt: { xs: 1.5, sm: 2.25 },
          pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: 'none',
          left: 0,
          right: 0,
          margin: '0 auto',
          zIndex: zIndex + 1,
        }
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" mb={2} sx={{ flexShrink: 0, px: { xs: 0.75, sm: 0 } }}>
        <Box sx={{ width: 40, flexShrink: 0 }} />
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            {t('comments')}
          </Typography>
          {listaComentarios.length > 0 && (
            <Box sx={{ bgcolor: 'rgba(0,212,255,0.2)', borderRadius: '12px', px: 1.2, py: 0.4 }}>
              <Typography variant="caption" sx={{ color: '#00d4ff', fontWeight: 700 }}>
                {listaComentarios.length}
              </Typography>
            </Box>
          )}
        </Stack>
        <IconButton onClick={handleClose} sx={{ color: onSurface(1), flexShrink: 0 }}><Close /></IconButton>
      </Stack>

      {/* Lista */}
      <List sx={{ flexGrow: 1, overflowY: 'auto', px: { xs: 0.75, sm: 0 }, minHeight: 0, pr: 0, pb: 1 }}>
        {listaComentarios.length === 0 && (
          <Box
            sx={{
              height: '100%',
              minHeight: 320,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 1.4,
              px: 3,
            }}
          >
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: '50%',
                bgcolor: 'rgba(0,212,255,0.08)',
                border: '1px solid rgba(0,212,255,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChatBubbleOutline sx={{ fontSize: 30, color: '#00d4ff' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: onSurface(1) }}>
              {t('noCommentsTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: onSurface(0.55), maxWidth: 260 }}>
              {t('noCommentsSubtitle')}
            </Typography>
          </Box>
        )}
        {listaComentarios.map((c, idx) => (
          <Box key={c.id}>
            <ListItem alignItems="flex-start" sx={{ py: { xs: 1.1, sm: 1.5 }, px: 0 }}>
              <ListItemAvatar sx={{ minWidth: { xs: 36, sm: 52 } }}>
                <Avatar 
                  src={c.userImg} 
                  onClick={() => goToProfile(c.uid)} 
                  sx={{ width: { xs: 28, sm: 40 }, height: { xs: 28, sm: 40 }, cursor: 'pointer', border: '1px solid rgba(0,212,255,0.3)' }} 
                />
              </ListItemAvatar>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="subtitle2"
                    sx={{ color: '#00d4ff', fontWeight: 600, cursor: 'pointer', maxWidth: { xs: '78%', sm: '100%' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    onClick={() => goToProfile(c.uid)}
                  >
                    {c.username}
                  </Typography>
                  {user?.uid === c.uid && (
                    <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuCommentId(c.id); }} sx={{ color: onSurface(0.4), p: { xs: 0.5, sm: 1 } }}>
                      <MoreVert sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Stack>

                {editingId === c.id ? (
                  <Box mt={0.5}>
                    <TextField
                      fullWidth
                      size="small"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveEdit(c.id);
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => handleSaveEdit(c.id)} disabled={!editText.trim()} sx={{ color: '#00d4ff' }}>
                              <Send sx={{ fontSize: 18 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { color: onSurface(1), bgcolor: onSurface(0.05), borderRadius: '10px' } }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: onSurface(1), mt: 0.5, wordBreak: 'break-word', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                    {c.text}
                  </Typography>
                )}

                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <IconButton size="small" onClick={() => toggleLike(c.id, c.likedByUsers)} sx={{ color: c.likedByUsers?.includes(user?.uid) ? '#ff6b9d' : onSurface(0.5), p: 0.5 }}>
                      {c.likedByUsers?.includes(user?.uid) ? <Favorite sx={{ fontSize: 16 }} /> : <FavoriteBorder sx={{ fontSize: 16 }} />}
                    </IconButton>
                    <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{c.likesCount || 0}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'pointer' }} onClick={() => setReplyingTo(c.id)}>
                    <ChatBubbleOutline sx={{ fontSize: 16, color: onSurface(0.5) }} />
                    <Typography variant="caption" sx={{ color: onSurface(0.6) }}>{c.repliesCount || 0}</Typography>
                  </Stack>
                </Stack>

                {replyingTo === c.id && (
                  <Box sx={{ mt: 1.5 }}>
                    <TextField
                      autoFocus
                      fullWidth
                      size="small"
                      placeholder={t('replyPlaceholder')}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          enviarComentario();
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={enviarComentario} disabled={!replyText.trim()} sx={{ color: '#00d4ff' }}>
                              <Send sx={{ fontSize: 18 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { color: onSurface(1), bgcolor: 'rgba(0,212,255,0.05)', borderRadius: '10px' } }}
                    />
                  </Box>
                )}
              </Box>
            </ListItem>

            <ListaRespuestas
              postId={postId}
              commentId={c.id}
              goToProfile={goToProfile}
              isLightTheme={isLightTheme}
              user={user}
              t={t}
              onSurface={onSurface}
              zIndex={zIndex}
              surfaceBg={surfaceBg}
            />
            {idx < listaComentarios.length - 1 && <Divider sx={{ bgcolor: onSurface(0.08), my: 1 }} />}
          </Box>
        ))}
      </List>

      {/* Input Principal */}
      <Box
        sx={{
          pt: 1.5,
          borderTop: `1px solid ${onSurface(0.1)}`,
          bgcolor: isLightTheme ? 'rgba(255,255,255,0.95)' : 'rgba(26,26,26,0.95)',
          backdropFilter: 'blur(6px)',
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          mt: 1,
          mx: { xs: -0.25, sm: -2.25 },
          px: { xs: 1.25, sm: 2.25 },
        }}
      >
        <TextField
          fullWidth
          placeholder={t('writeComment')}
          size="small"
          value={nuevoComentario}
          onChange={(e) => setNuevoComentario(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              enviarComentarioNuevo();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={enviarComentarioNuevo}
                  sx={{
                    color: '#00d4ff',
                    bgcolor: 'rgba(0,212,255,0.1)',
                    '&:hover': { bgcolor: 'rgba(0,212,255,0.2)' },
                    width: { xs: 30, sm: 34 },
                    height: { xs: 30, sm: 34 },
                  }}
                >
                  <Send sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: onSurface(1),
              bgcolor: onSurface(0.05),
              borderRadius: '12px',
              minHeight: isMobile ? 42 : 'auto',
              pr: 0.75,
            },
          }}
        />
      </Box>

      {/* Menú Contextual */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} sx={{ zIndex: zIndex + 2 }} PaperProps={{ sx: { bgcolor: surfaceBg, color: onSurface(1) } }}>
        <MenuItem onClick={() => { const c = listaComentarios.find(x => x.id === menuCommentId); setEditingId(c.id); setEditText(c.text); setMenuAnchor(null); }}>
          <Edit sx={{ fontSize: 18, mr: 1, color: '#00d4ff' }} /> {t('edit')}
        </MenuItem>
        <MenuItem onClick={() => handleDeleteComment(menuCommentId)}>
          <Delete sx={{ fontSize: 18, mr: 1, color: '#ff6b6b' }} /> {t('delete')}
        </MenuItem>
      </Menu>
    </Drawer>
  );
};

export default Comentarios;